/* eslint-disable consistent-return */
import { Meteor } from 'meteor/meteor';
import { check } from 'meteor/check';
import { Roles } from 'meteor/alanning:roles';
import { concat, filter, find, forEach, isNil } from 'lodash';

import AnnotationTemplates from '../AnnotationTemplates';
import * as templateOps from '../annotation-template-operations';

import handleMethodException from '../../../modules/handle-method-exception';

const editTemplateSchema = AnnotationTemplates.schema.omit(
  'owner',
  'createdAt',
  'updatedAt',
  'deleted'
);
editTemplateSchema.extend({ _id: String });

/**
 * Inserts the annotation template in the database.
 *
 * @param {object} annotationTemplate - The annotation template object.
 * @param {string} userId - The user id that owns the annotation template.
 * @returns {string} The id of the new annotation template inserted in the database.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const insert = (annotationTemplate, userId) => {
  try {
    if (Roles.userIsInRole(userId, 'admin')) {
      AnnotationTemplates.schema.validate(annotationTemplate, {
        check,
        keys: ['name', 'icon', 'tags', 'elements']
      });
      return AnnotationTemplates.insert({ owner: userId, ...annotationTemplate });
    }
    throw new Meteor.Error('403', 'Sorry, you need to be an administrator to do this.');
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Updates the annotation template in the database.
 *
 * @param {object} annotationTemplate - The new annotation template object.
 * @param {string} userId - The user id that owns the annotation template.
 * @returns {string} The id of the updated annotation template in the database.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const update = (annotationTemplate, userId) => {
  try {
    if (Roles.userIsInRole(userId, 'admin')) {
      editTemplateSchema.validate(annotationTemplate);
      const annotationTemplateId = templateOps.getId(annotationTemplate);
      AnnotationTemplates.update(annotationTemplateId, { $set: annotationTemplate });
      return annotationTemplateId;
    }
    throw new Meteor.Error('403', 'Sorry, you need to be an administrator to do this.');
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Soft deletes the annotation template in the database.
 *
 * @param {string} annotationTemplateId - The annotation template id.
 * @param {string} userId - The user id that owns the annotation template.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const softDelete = (annotationTemplateId, userId) => {
  check(annotationTemplateId, String);
  try {
    if (Roles.userIsInRole(userId, 'admin')) {
      AnnotationTemplates.update(annotationTemplateId, {
        $set: { deleted: new Date().toISOString() }
      });
    } else {
      throw new Meteor.Error('403', 'Sorry, you need to be an administrator to do this.');
    }
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Restores the annotation template in the database.
 *
 * @param {string} annotationTemplateId - The annotation template id.
 * @param {string} userId - The user id that owns the annotation template.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const restore = (annotationTemplateId, userId) => {
  check(annotationTemplateId, String);
  try {
    if (Roles.userIsInRole(userId, 'admin')) {
      AnnotationTemplates.update(annotationTemplateId, { $set: { deleted: 'no' } });
    } else {
      throw new Meteor.Error('403', 'Sorry, you need to be an administrator to do this.');
    }
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Deletes the annotation template from database.
 *
 * @param {string} annotationTemplateId - The annotation template id.
 * @param {string} userId - The user id that owns the annotation template.
 * @returns {string} The id of the deleted annotation template.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const hardDelete = (annotationTemplateId, userId) => {
  check(annotationTemplateId, String);
  try {
    if (Roles.userIsInRole(userId, 'admin')) {
      return AnnotationTemplates.remove(annotationTemplateId);
    }
    throw new Meteor.Error('403', 'Sorry, you need to be an administrator to do this.');
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Get the suggestion for the tags from the database annotation templates.
 *
 * @returns {object[]} An array with the existing tags from the annotation templates in the database.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const getTagSuggestions = () => {
  let suggestions = [];
  const annotationTemplates = AnnotationTemplates.find({ deleted: { $eq: 'no' } }).fetch();
  forEach(annotationTemplates, template => {
    suggestions = concat(
      suggestions,
      filter(templateOps.getTags(template), tag => isNil(find(suggestions, tag)))
    );
  });
  return suggestions;
};
